import React, { useState, useRef, useCallback, useEffect, useContext } from 'react';
import { CameraOutlined, PlusOutlined, ScanOutlined, EditOutlined, SearchOutlined, RotateRightOutlined, ReloadOutlined, CheckOutlined, FilterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../../Constants';
import AppContext from '../../AppContext';
import { Select, DatePicker } from 'antd';
import { toast } from 'react-toastify';
import { parseExtractedData } from './businessCardParser';
import { captureGuideArea, shrinkImage, rotateImage } from './cardImage';
import BusinessCardForm from './BusinessCardForm';
import CardPhotoViewer from './CardPhotoViewer';
import CardCropper from './CardCropper';import SavedCardItem from './SavedCardItem';

const EMPTY_CARD = { name: '', company: '', position: '', email: '', phone: '', website: '', address: '', tag: '', notes: '' };
const DETECTED_FIELDS = ['name', 'position', 'company', 'phone', 'email', 'website', 'address'];

const detectFields = (parsed) =>
  Object.fromEntries(
    DETECTED_FIELDS.map((f) => [f, parsed.guessed?.includes(f) ? 'guessed' : parsed[f] ? 'found' : 'missing'])
  );

const dataUrlToBlob = (dataUrl) => {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: mime });
};

const BusinessCardScanner = () => {
  const { t } = useTranslation();
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [dateRange, setDateRange] = useState(null); // [start, end] days, or null
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount = (selectedTag ? 1 : 0) + (dateRange ? 1 : 0);
  const [page, savePage] = useState(1);
  const pageSize = 9;
  const [hasMore, setHasMore] = useState(false);
  const authCtx = useContext(AppContext);
  const employeeId = authCtx?.profile?.publicEmployeeId;
  const [capturedImage, setCapturedImage] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [totalCount, setTotalCount] = useState(null); // all matching cards, from X-Total-Count
  const [searchTerm, setSearchTerm] = useState('');
  // searchTerm follows every keystroke; debouncedSearch follows it 300ms after typing stops,
  // so the server is asked once per pause instead of once per letter.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [editingCard, setEditingCard] = useState(null);
  const [captureSource, setCaptureSource] = useState(null); // 'camera' | 'upload'
  const [detected, setDetected] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [backImage, setBackImage] = useState(null);
  const [savedPhotoViewer, setSavedPhotoViewer] = useState(null);
  const [cropping, setCropping] = useState(null); // { image, side: 'front' | 'back' }
  const fileInputRef = useRef(null);
  const backInputRef = useRef(null);
  const videoRef = useRef(null);
  const guideRef = useRef(null);
  // Bumped on every new scan so a slow OCR reply for a discarded photo is ignored.
  const scanIdRef = useRef(0);

  const stopCamera = () => {
    const stream = videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
  };

  useEffect(() => () => {
    const stream = videoRef.current?.srcObject;
    if (stream) stream.getTracks().forEach(track => track.stop());
  }, []);

  const resetScan = () => {
    scanIdRef.current += 1;
    setCapturedImage(null);
    setExtractedData(null);
    setDetected(null);
    setViewerOpen(false);
    setBackImage(null);
  };

  // Extract text from image via the backend OCR proxy (Azure Read API server-side)
  const extractTextFromImage = async (imageData) => {
    const scanId = ++scanIdRef.current;
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('image', dataUrlToBlob(imageData), 'card.jpg');

      const response = await fetch(`${BASE_URL}/ocr/business-card`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authCtx?.profile?.token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OCR request failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      const parsed = parseExtractedData(result);
      // OCR tells us how far the photo is turned; show it upright.
      const upright = parsed.rotation ? await rotateImage(imageData, parsed.rotation) : imageData;
      if (scanId !== scanIdRef.current) return;
      setCapturedImage(upright);
      setExtractedData(parsed);
      setDetected(detectFields(parsed));
    } catch (error) {
      console.error('Error extracting text:', error);
      if (scanId !== scanIdRef.current) return;
      // Still let the user type the details in by hand.
      toast.error(t('businessCardScanner.readFailed'));
      setExtractedData({ ...EMPTY_CARD });
      setDetected(null);
    } finally {
      if (scanId === scanIdRef.current) setIsProcessing(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    event.target.value = ''; // allow picking the same file again after Retake
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const image = await shrinkImage(e.target.result).catch(() => e.target.result);
        setCaptureSource('upload');
        setCropping({ image, side: 'front' });
      };
      reader.readAsDataURL(file);
    }
  };

  // The back of a card is only stored as a photo (no text reading).
  const handleBackUpload = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const image = await shrinkImage(e.target.result).catch(() => e.target.result);
      setCropping({ image, side: 'back' });
    };
    reader.readAsDataURL(file);
  };

  // The cropped (or full) photo comes back from the crop step.
  const handleCropDone = (image) => {
    const { side } = cropping;
    setCropping(null);
    if (side === 'back') {
      setBackImage(image);
    } else {
      setCapturedImage(image);
      extractTextFromImage(image);
    }
  };

  const uploadCardPhoto = async (cardId, side, dataUrl) => {
    const formData = new FormData();
    formData.append('image', dataUrlToBlob(dataUrl), `${side}.jpg`);
    const response = await fetch(`${BASE_URL}/businesscards/${cardId}/images/${side}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${authCtx?.profile?.token}` },
      body: formData
    });
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  };

  const startCamera = async () => {
    setCameraLoading(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      setShowCamera(true);

      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().catch(err => {
              console.error('Video play failed:', err);
            });
          };
        }
      }, 100);

    } catch (error) {
      console.error('Error accessing camera:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Camera access denied. Please allow camera permissions and try again.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else {
        toast.error('Camera access failed: ' + error.message);
      }
      setShowCamera(false);
    } finally {
      setCameraLoading(false);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;

    if (video && guideRef.current && video.videoWidth > 0 && video.videoHeight > 0) {
      // Keep only the part of the frame inside the card outline
      const imageData = captureGuideArea(video, guideRef.current);
      setCaptureSource('camera');
      setCropping({ image: imageData, side: 'front' });
      stopCamera();
    } else {
      toast.error('Camera not ready. Please wait a moment and try again.');
    }
  };

  const retake = () => {
    resetScan();
    if (captureSource === 'upload') {
      fileInputRef.current?.click();
    } else {
      startCamera();
    }
  };

  const rotateCaptured = async () => {
    setCapturedImage(await rotateImage(capturedImage, 90));
  };

  const saveBusinessCard = async () => {
    if (!employeeId) {
      toast.error('You must be logged in to save business cards.');
      return;
    }
    if (extractedData && !extractedData.name?.trim()) {
      toast.error(t('businessCardScanner.nameRequired'));
      return;
    }
    if (extractedData) {
      try {
        const normalize = (v) => (v && v.toString().trim() ? v.toString().trim() : null);
        const cardData = {
          name: normalize(extractedData.name),
          company: normalize(extractedData.company),
          position: normalize(extractedData.position),
          email: normalize(extractedData.email),
          phone: normalize(extractedData.phone),
          website: normalize(extractedData.website),
          address: normalize(extractedData.address),
          tag: normalize(extractedData.tag),
          notes: normalize(extractedData.notes),
          userId: employeeId
        };

        const response = await fetch(`${BASE_URL}/businesscards`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authCtx?.profile?.token}`
          },
          body: JSON.stringify(cardData)
        });

        if (response.ok) {
          let savedCard = await response.json();
          // The details are saved; the photos follow. A failed photo must not lose the contact.
          let photoFailed = false;
          for (const [side, photo] of [['front', capturedImage], ['back', backImage]]) {
            if (!photo) continue;
            try {
              savedCard = await uploadCardPhoto(savedCard.id, side, photo);
            } catch (photoError) {
              console.error(`Error uploading ${side} photo:`, photoError);
              photoFailed = true;
            }
          }
          setSavedCards([savedCard, ...savedCards]);
          setTotalCount((n) => (n == null ? n : n + 1));
          resetScan();
          if (photoFailed) {
            toast.warning(t('businessCardScanner.photoSaveFailed'));
          } else {
            toast.success('Business card saved successfully!');
          }
        } else {
          const error = await response.text();
          toast.error(`Failed to save: ${error}`);
        }
      } catch (error) {
        console.error('Error saving to API:', error);
        toast.error('Network error: Unable to save business card. Please check your connection and try again.');
      }
    }
  };

  const loadSavedCards = useCallback(async () => {
    if (!employeeId) {
      return;
    }
    let url = `${BASE_URL}/businesscards?userId=${employeeId}&page=${page}&pageSize=9`;
    // Search runs on the server, before paging, so it finds cards on every page.
    if (debouncedSearch.trim()) {
      url += `&search=${encodeURIComponent(debouncedSearch.trim())}`;
    }
    if (selectedTag) {
      url += `&tag=${encodeURIComponent(selectedTag)}`;
    }
    if (dateRange?.[0] && dateRange?.[1]) {
      // Whole local days, sent as the instants they start and end at.
      url += `&from=${encodeURIComponent(dateRange[0].startOf('day').toISOString())}`;
      url += `&to=${encodeURIComponent(dateRange[1].add(1, 'day').startOf('day').toISOString())}`;
    }

    try {
      const response = await fetch(url, {

        headers: {
          'Authorization': `Bearer ${authCtx?.profile?.token}`
        }
      });

      if (response.ok) {
        const cards = await response.json();
        const total = parseInt(response.headers.get('X-Total-Count'), 10);
        setSavedCards(cards);
        setTotalCount(Number.isNaN(total) ? null : total);
        // Prefer the real total; an older API without the header falls back to "a full page means maybe more".
        setHasMore(Number.isNaN(total)
          ? (Array.isArray(cards) ? cards.length === pageSize : false)
          : page * pageSize < total);
      } else {
        console.error('Failed to load cards:', await response.text());
      }
    } catch (error) {
      console.error('Error loading cards:', error);
      toast.error('Network error: Unable to load saved cards. Please check your connection.');
    }
  }, [employeeId, authCtx?.profile?.token, page, selectedTag, dateRange, debouncedSearch]);

  useEffect(() => {
    loadSavedCards();
  }, [loadSavedCards]);

  // Wait for typing to pause, then search from page 1 (page 3 of the old list means nothing for a new search).
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== debouncedSearch) {
        setDebouncedSearch(searchTerm);
        savePage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearch]);

  // Tags for the filter dropdown come from all of the user's cards, not just the page on screen.
  // Re-read whenever the list changes, so a tag added, edited or deleted shows up straight away.
  useEffect(() => {
    if (!employeeId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${BASE_URL}/businesscards/tags`, {
          headers: { 'Authorization': `Bearer ${authCtx?.profile?.token}` }
        });
        if (!response.ok) return;
        const tags = await response.json();
        if (!cancelled && Array.isArray(tags)) {
          setAvailableTags(Array.from(new Set(tags.filter((tag) => typeof tag === 'string' && tag))).sort());
        }
      } catch (error) {
        console.error('Error loading tags:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [employeeId, authCtx?.profile?.token, savedCards]);

  const deleteCard = async (cardId) => {
    try {
      const response = await fetch(`${BASE_URL}/businesscards/${cardId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authCtx?.profile?.token}`
        }
      });

      if (response.ok) {
        setSavedCards(savedCards.filter(card => card.id !== cardId));
        setTotalCount((n) => (n == null ? n : Math.max(0, n - 1)));
        toast.success('Business card deleted successfully!');
      } else {
        const error = await response.text();
        toast.error(`Failed to delete: ${error}`);
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error('Network error: Unable to delete business card. Please check your connection and try again.');
    }
  };
  const clearFilters = () => {
    setSelectedTag(null);
    setDateRange(null);
    setSearchTerm('');
    setDebouncedSearch('');
    savePage(1);
  };
  // The server already applied search, tag and date filters.
  const filteredCards = savedCards;

  return (
    <div className="max-w-6xl mx-auto p-0 lg:p-6 space-y-6">
      {/* Header */}
      <div className="text-center block lg:hidden">
        <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center justify-center">
          <ScanOutlined className="me-2" />
          {t('businessCardScanner.title')}
        </h2>
        <p className="text-sm text-gray-600">{t('businessCardScanner.subtitle')}</p>
      </div>

      {/* Scanner Section */}
      <div className="block lg:hidden">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
        />
        <input
          ref={backInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleBackUpload}
          className="hidden"
        />

        {!showCamera && !capturedImage && !cameraLoading && (
          <div className="flex gap-3">
            <button
              onClick={startCamera} type="button"
              className="flex-1 flex items-center justify-center px-3 py-3 rounded-xl bg-blue-600 text-white shadow-sm hover:bg-blue-700 active:scale-95 transition-all font-semibold"
            >
              <CameraOutlined className="me-2 text-lg" />
              <span>{t('businessCardScanner.takePhoto')}</span>
            </button>

            <button
              onClick={() => fileInputRef.current?.click()} type="button"
              className="flex-1 flex items-center justify-center px-3 py-3 rounded-xl border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:scale-95 transition-all font-semibold"
            >
              <PlusOutlined className="me-2 text-lg" />
              <span>{t('businessCardScanner.uploadImage')}</span>
            </button>
          </div>
        )}

        {/* Camera Loading */}
        {cameraLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">{t('businessCardScanner.startingCamera')}</p>
              <p className="text-sm text-gray-500 mt-2">{t('businessCardScanner.allowCamera')}</p>
            </div>
          </div>
        )}

        {/* Camera View: landscape card outline, everything outside it darkened */}
        {showCamera && (
          <div className="space-y-4">
            <div className="relative w-full aspect-[3/4] max-h-[70vh] bg-black rounded-2xl overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  ref={guideRef}
                  className="w-[88%] aspect-[7/4] rounded-xl border-2 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]"
                />
              </div>
              <p className="absolute top-4 inset-x-4 text-center text-sm font-medium text-white pointer-events-none">
                {t('businessCardScanner.cameraHint')}
              </p>
            </div>
            <div className="flex items-center justify-between px-4">
              <button
                onClick={stopCamera} type="button"
                className="w-20 text-start text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                {t('businessCardScanner.cancel')}
              </button>
              <button
                onClick={capturePhoto} type="button"
                aria-label={t('businessCardScanner.capture')}
                className="w-16 h-16 rounded-full bg-white border-4 border-blue-600 shadow-lg flex items-center justify-center active:scale-95 transition-transform"
              >
                <span className="w-12 h-12 rounded-full bg-blue-600" />
              </button>
              <span className="w-20" />
            </div>
          </div>
        )}

        {/* Captured Image and Extraction */}
        {capturedImage && (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="relative w-full aspect-[7/4] bg-gray-900 rounded-2xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setViewerOpen(true)}
                  className="absolute inset-0 w-full h-full"
                  aria-label={t('businessCardScanner.enlarge')}
                >
                  <img
                    src={capturedImage}
                    alt={t('businessCardScanner.photoAlt')}
                    className="w-full h-full object-contain"
                  />
                </button>
                {isProcessing ? (
                  <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2"></div>
                    <p className="text-sm font-medium">{t('businessCardScanner.reading')}</p>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={rotateCaptured}
                    aria-label={t('businessCardScanner.rotate')}
                    title={t('businessCardScanner.rotate')}
                    className="absolute bottom-3 end-3 w-11 h-11 rounded-full bg-white/90 shadow-md flex items-center justify-center text-gray-700 hover:bg-white"
                  >
                    <RotateRightOutlined />
                  </button>
                )}
              </div>
              {!isProcessing && (
                <p className="text-xs text-gray-400 mt-2 text-center">{t('businessCardScanner.enlarge')}</p>
              )}
            </div>

            {extractedData && !isProcessing && (
              <div>
                <div className="bg-white rounded-2xl border border-gray-200 p-4">
                  <h5 className="text-base font-semibold text-gray-900 flex items-center">
                    <EditOutlined className="me-2 text-blue-600" />
                    {t('businessCardScanner.reviewTitle')}
                  </h5>
                  <p className="text-xs text-gray-500 mt-1 mb-4">{t('businessCardScanner.reviewHint')}</p>
                  <BusinessCardForm
                    value={extractedData}
                    onChange={setExtractedData}
                    availableTags={availableTags}
                    detected={detected}
                  />
                </div>

                {/* Optional photo of the back of the card */}
                <div className="mt-4 bg-white rounded-2xl border border-gray-200 p-4">
                  <h5 className="text-base font-semibold text-gray-900">{t('businessCardScanner.backSide')}</h5>
                  {backImage ? (
                    <div className="mt-3">
                      <img
                        src={backImage}
                        alt={t('businessCardScanner.backSide')}
                        className="w-full aspect-[7/4] object-contain bg-gray-900 rounded-xl"
                      />
                      <div className="flex gap-4 mt-2 text-sm font-medium">
                        <button type="button" onClick={() => backInputRef.current?.click()} className="text-blue-600">
                          {t('businessCardScanner.retakeBack')}
                        </button>
                        <button type="button" onClick={() => setBackImage(null)} className="text-red-600">
                          {t('businessCardScanner.removeBack')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => backInputRef.current?.click()}
                      className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 font-medium hover:border-blue-400 hover:text-blue-700"
                    >
                      <CameraOutlined />
                      {t('businessCardScanner.addBack')}
                    </button>
                  )}
                </div>

                {/* Stays on screen while the form scrolls */}
                <div
                  className="sticky bottom-0 z-10 mt-4 flex gap-3 bg-white/95 backdrop-blur rounded-t-xl border-t border-gray-200 px-1 pt-3 shadow-[0_-6px_12px_-8px_rgba(0,0,0,0.15)]"
                  style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
                >
                  <button
                    onClick={retake} type="button"
                    className="px-4 py-3 rounded-xl border border-gray-300 bg-white text-gray-700 font-semibold flex items-center gap-2 hover:bg-gray-50"
                  >
                    <ReloadOutlined />
                    {t('businessCardScanner.retake')}
                  </button>
                  <button
                    onClick={saveBusinessCard} type="button"
                    className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-semibold flex items-center justify-center gap-2 shadow-md hover:bg-blue-700"
                  >
                    <CheckOutlined />
                    {t('businessCardScanner.saveContact')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {cropping && (
          <CardCropper
            image={cropping.image}
            title={t(cropping.side === 'back' ? 'businessCardScanner.cropTitleBack' : 'businessCardScanner.cropTitle')}
            onDone={handleCropDone}
            onCancel={() => setCropping(null)}
          />
        )}

        {/* Full-screen photo */}
        {viewerOpen && capturedImage && (
          <CardPhotoViewer front={capturedImage} back={backImage} onClose={() => setViewerOpen(false)} />
        )}
      </div>

      {/* Saved Cards Section */}
      <div>
        <h3 className="text-lg font-semibold mb-3">
          {t('businessCardScanner.savedTitle')} <span className="text-gray-400 font-normal">({totalCount ?? savedCards.length})</span>
        </h3>

        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 flex items-center rounded-lg focus-within:ring-2 focus-within:ring-blue-500 bg-white border border-gray-300">
            <SearchOutlined className="text-gray-400 ms-3" />
            <input
              type="text"
              placeholder={t('businessCardScanner.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-0 ps-2 pe-4 py-2 focus:outline-none rounded-lg bg-transparent"
            />
          </div>
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            className={`shrink-0 h-10 px-3 rounded-lg border flex items-center gap-1.5 text-sm font-medium ${
              activeFilterCount ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700'
            }`}
          >
            <FilterOutlined />
            {t('businessCardScanner.filters')}
            {activeFilterCount > 0 && (
              <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[11px] flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {filtersOpen && (
          <div className="mb-4 p-3 rounded-xl border border-gray-200 bg-white flex flex-wrap items-center gap-2">
            <Select
              style={{ width: 140 }}
              placeholder={t('businessCardScanner.filterTag')}
              allowClear
              value={selectedTag}
              onChange={(val) => {
                setSelectedTag(val);
                savePage(1);
              }}
              options={availableTags.map((tag) => ({ label: tag, value: tag }))}
            />
            <DatePicker.RangePicker
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
                savePage(1);
              }}
              allowClear
              disabledDate={(day) => day.isAfter(new Date(), 'day')}
              placeholder={[t('businessCardScanner.dateFrom'), t('businessCardScanner.dateTo')]}
              style={{ width: 230 }}
            />
            {activeFilterCount > 0 && (
              <button type="button" onClick={clearFilters} className="text-sm font-medium text-red-600 px-1">
                {t('businessCardScanner.clearFilters')}
              </button>
            )}
          </div>
        )}


        {filteredCards.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? 'No cards match your search.' : 'No business cards saved yet. Start by scanning your first card!'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredCards.map((card) => (
              <SavedCardItem
                key={card.id}
                card={card}
                onEdit={setEditingCard}
                onDelete={deleteCard}
                onViewPhotos={setSavedPhotoViewer}
              />
            ))}
          </div>
        )}
        <div className="mt-6 flex justify-center">
          <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white shadow-sm">
            <button
              disabled={page === 1}
              onClick={() => savePage(p => Math.max(1, p - 1))}
              className="px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed rounded-l-lg border-r border-gray-200 transition-colors"
            >
              ‹
            </button>
            <span className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-50 min-w-[28px] text-center">{page}</span>
            <button
              disabled={!hasMore}
              onClick={() => savePage(p => p + 1)}
              className="px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed rounded-r-lg border-l border-gray-200 transition-colors"
            >
              ›
            </button>
          </div>
        </div>

      </div>

      {/* Full-screen view of a saved card photo */}
      {savedPhotoViewer && (
        <CardPhotoViewer
          front={savedPhotoViewer.front}
          back={savedPhotoViewer.back}
          onClose={() => setSavedPhotoViewer(null)}
        />
      )}

      {/* Edit Modal */}
      {editingCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">{t('businessCardScanner.editTitle')}</h3>
            <BusinessCardForm
              value={editingCard}
              onChange={setEditingCard}
              availableTags={availableTags}
            />
            <div className="flex gap-3 mt-6">
              <button
                onClick={async () => {
                  try {
                    console.log(BASE_URL, editingCard.id);
                    const response = await fetch(`${BASE_URL}/businesscards/${editingCard.id}`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authCtx?.profile?.token}`
                      },
                      body: JSON.stringify(editingCard)
                    });

                    if (response.ok) {
                      const updatedCard = await response.json();
                      setSavedCards(savedCards.map(card =>
                        card.id === editingCard.id ? updatedCard : card
                      ));
                      setEditingCard(null);
                      toast.success('Business card updated successfully!');
                    } else {
                      const error = await response.text();
                      toast.error(`Failed to update: ${error}`);
                    }
                  } catch (error) {
                    console.error('Error updating card:', error);
                    toast.error('Network error: Unable to update business card. Please check your connection and try again.');
                  }
                }}
                type='button'
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {t('businessCardScanner.saveChanges')}
              </button>
              <button
                onClick={() => setEditingCard(null)} type='button'
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                {t('businessCardScanner.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessCardScanner;